/**
 * Brush primitives (sphere, capsule) and exact per-row x-spans.
 *
 * Geometry is evaluated in 1/16-voxel fixed point (see fixed.ts). `insideFx` is the single source
 * of truth: a cell is inside iff its centre is within `rFx` of the primitive (distance <= r, matching
 * `rasterizeEllipsoid`'s `<= 1` rule). `rowSpan` returns, for one (y, z) row, the exact interval of
 * cell x indices whose centres satisfy `insideFx`. It uses a float estimate followed by an integer
 * fix-up against `insideFx`, so the span is bit-exact: `rowSpan` and a brute-force scan of `insideFx`
 * always agree.
 *
 * Exactness of `insideFx` for the capsule relies on the integer products `|p-a|^2 * |d|^2` staying
 * below 2^53. With document coordinates <= 512 voxels (8192 Fx) that holds whenever the segment is
 * shorter than about 2^26 / 8192^2 ... in practice: |d| <= ~1000 voxels, far beyond the sampler's
 * break distance (`max(10, 2r)` voxels). Both `insideFx` and `rowSpan` use the same arithmetic, so
 * they agree with each other even for pathological payloads.
 */

import { FX_ONE, type Fx3 } from './fixed'

export type SpherePrim = { readonly kind: 'sphere'; readonly c: Fx3; readonly rFx: number }
export type CapsulePrim = { readonly kind: 'capsule'; readonly a: Fx3; readonly b: Fx3; readonly rFx: number }
export type BrushPrim = SpherePrim | CapsulePrim

export function sphere(c: Fx3, rFx: number): SpherePrim {
  return { kind: 'sphere', c, rFx }
}

export function capsule(a: Fx3, b: Fx3, rFx: number): CapsulePrim {
  return { kind: 'capsule', a, b, rFx }
}

// ----------------------------------------------------------------------------
// Exact predicates (integer fixed point)
// ----------------------------------------------------------------------------

/** Exact: is the centre of cell (x, y, z) inside the primitive? */
export function insideFx(p: BrushPrim, x: number, y: number, z: number): boolean {
  return insideAtRadiusFx(p, x, y, z, p.rFx)
}

/**
 * Exact: is the centre of cell (x, y, z) within `rFx` of the primitive's core (the centre for a
 * sphere, the segment for a capsule)? `insideFx` with the primitive's own radius; the lumpy sweep
 * (sweep.ts) calls it with a per-cell effective radius (noise.ts).
 */
export function insideAtRadiusFx(p: BrushPrim, x: number, y: number, z: number, rFx: number): boolean {
  const X = x * FX_ONE, Y = y * FX_ONE, Z = z * FX_ONE
  if (p.kind === 'sphere') {
    const dx = X - p.c[0], dy = Y - p.c[1], dz = Z - p.c[2]
    return dx * dx + dy * dy + dz * dz <= rFx * rFx
  }
  return capsuleContainsFx(p, X, Y, Z, rFx)
}

/** Exact capsule test on a fixed-point point (X, Y, Z), at the capsule's radius unless `rFx` is given. */
export function capsuleContainsFx(p: CapsulePrim, X: number, Y: number, Z: number, rFx: number = p.rFx): boolean {
  const ax = p.a[0], ay = p.a[1], az = p.a[2]
  const dx = p.b[0] - ax, dy = p.b[1] - ay, dz = p.b[2] - az
  const r2 = rFx * rFx
  const px = X - ax, py = Y - ay, pz = Z - az
  const P2 = px * px + py * py + pz * pz
  const L2 = dx * dx + dy * dy + dz * dz
  if (L2 === 0) return P2 <= r2
  const dot = px * dx + py * dy + pz * dz
  if (dot <= 0) return P2 <= r2
  if (dot >= L2) {
    const qx = X - p.b[0], qy = Y - p.b[1], qz = Z - p.b[2]
    return qx * qx + qy * qy + qz * qz <= r2
  }
  // dist^2 = P2 - dot^2 / L2  <=  r2   <=>   P2*L2 - dot^2 <= r2*L2   (all integers)
  return P2 * L2 - dot * dot <= r2 * L2
}

// ----------------------------------------------------------------------------
// Row spans
// ----------------------------------------------------------------------------

export type Span = [number, number]

/**
 * Exact interval of cell x indices in row (y, z) whose centres are inside `p`, or null when empty.
 * Distance to a convex set is convex along a line, so the inside set is always one interval.
 */
export function rowSpan(p: BrushPrim, y: number, z: number): Span | null {
  return p.kind === 'sphere' ? sphereRowSpan(p, y, z) : capsuleRowSpan(p, y, z)
}

function sphereRowSpan(p: SpherePrim, y: number, z: number): Span | null {
  const cx = p.c[0]
  const dy = y * FX_ONE - p.c[1]
  const dz = z * FX_ONE - p.c[2]
  const d2 = p.rFx * p.rFx - dy * dy - dz * dz // exact integer
  if (d2 < 0) return null
  const w = Math.sqrt(d2)
  // Float estimate (sqrt of a perfect square is exact, so this is already exact in the common case)
  let x0 = Math.ceil((cx - w) / FX_ONE)
  let x1 = Math.floor((cx + w) / FX_ONE)
  const inside = (x: number) => {
    const dx = x * FX_ONE - cx
    return dx * dx <= d2
  }
  return fixupSpan(x0, x1, inside, Math.floor((cx - p.rFx) / FX_ONE) - 1, Math.ceil((cx + p.rFx) / FX_ONE) + 1)
}

// Tiny inflation of r^2 for the float cylinder estimate so exact tangencies are not lost to rounding.
// The integer fix-up removes any cell this lets through.
const EST_EPS = 1e-6

function capsuleRowSpan(p: CapsulePrim, y: number, z: number): Span | null {
  const Y = y * FX_ONE, Z = z * FX_ONE
  const ax = p.a[0], ay = p.a[1], az = p.a[2]
  const bx = p.b[0], by = p.b[1], bz = p.b[2]
  const r = p.rFx, r2 = r * r
  // Row outside the capsule's y/z extent: nothing to do.
  if (Y < Math.min(ay, by) - r || Y > Math.max(ay, by) + r) return null
  if (Z < Math.min(az, bz) - r || Z > Math.max(az, bz) + r) return null

  let lo = Number.POSITIVE_INFINITY, hi = Number.NEGATIVE_INFINITY
  // Sphere cap at a
  const dya = Y - ay, dza = Z - az
  const d2a = r2 - dya * dya - dza * dza
  if (d2a >= 0) { const w = Math.sqrt(d2a); lo = ax - w; hi = ax + w }
  // Sphere cap at b
  const dyb = Y - by, dzb = Z - bz
  const d2b = r2 - dyb * dyb - dzb * dzb
  if (d2b >= 0) { const w = Math.sqrt(d2b); if (bx - w < lo) lo = bx - w; if (bx + w > hi) hi = bx + w }
  // Cylinder body: points whose projection falls within the segment and whose distance to the axis <= r
  const dx = bx - ax, dy = by - ay, dz = bz - az
  const L2 = dx * dx + dy * dy + dz * dz
  if (L2 > 0) {
    const Cyz = dya * dya + dza * dza
    const Dyz = dya * dy + dza * dz
    // In u = X - ax:  q(u) = A u^2 + B u + C0  is the squared distance to the infinite axis
    const A = (dy * dy + dz * dz) / L2
    let ulo = Number.NEGATIVE_INFINITY, uhi = Number.POSITIVE_INFINITY
    let ok = true
    if (dy === 0 && dz === 0) {
      // Axis parallel to the row: distance is constant along X
      if (Cyz > r2) ok = false
    } else {
      const B = -2 * dx * Dyz / L2
      const C = Cyz - (Dyz * Dyz) / L2 - (r2 + EST_EPS)
      const disc = B * B - 4 * A * C
      if (disc < 0) ok = false
      else {
        const s = Math.sqrt(disc)
        ulo = (-B - s) / (2 * A)
        uhi = (-B + s) / (2 * A)
      }
    }
    if (ok) {
      // Projection constraint 0 <= dot <= L2 with dot = dx*u + Dyz
      if (dx > 0) { ulo = Math.max(ulo, -Dyz / dx); uhi = Math.min(uhi, (L2 - Dyz) / dx) }
      else if (dx < 0) { ulo = Math.max(ulo, (L2 - Dyz) / dx); uhi = Math.min(uhi, -Dyz / dx) }
      else if (Dyz < 0 || Dyz > L2) ok = false
      if (ok && ulo <= uhi) {
        if (ax + ulo < lo) lo = ax + ulo
        if (ax + uhi > hi) hi = ax + uhi
      }
    }
  }
  if (!(lo <= hi)) return null // float says the row misses the capsule (caps are exact; body has EST_EPS slack)

  let x0 = Math.ceil(lo / FX_ONE)
  let x1 = Math.floor(hi / FX_ONE)
  if (x0 > x1) {
    // No cell centre inside the float interval; the exact set can still hold one cell at either end.
    const t = x0; x0 = x1; x1 = t
  }
  const inside = (x: number) => capsuleContainsFx(p, x * FX_ONE, Y, Z)
  return fixupSpan(
    x0, x1, inside,
    Math.floor((Math.min(ax, bx) - r) / FX_ONE) - 1,
    Math.ceil((Math.max(ax, bx) + r) / FX_ONE) + 1,
  )
}

// Bound on how far the float estimate may be corrected before falling back to a full scan.
const MAX_FIXUP_STEPS = 4

/**
 * Turn a float estimate [x0, x1] into the exact inside interval using the exact predicate.
 * Requires the inside set to be a single interval (convexity). `lo`/`hi` bound a superset of the
 * inside set; if the estimate is further off than MAX_FIXUP_STEPS the superset is scanned instead.
 */
function fixupSpan(x0: number, x1: number, inside: (x: number) => boolean, lo: number, hi: number): Span | null {
  if (x0 < lo) x0 = lo
  if (x1 > hi) x1 = hi
  // Expand outward while the neighbour is still inside
  let steps = 0
  while (steps < MAX_FIXUP_STEPS && inside(x0 - 1)) { x0--; steps++ }
  if (steps === MAX_FIXUP_STEPS) return scanSpan(inside, lo, hi)
  steps = 0
  while (steps < MAX_FIXUP_STEPS && inside(x1 + 1)) { x1++; steps++ }
  if (steps === MAX_FIXUP_STEPS) return scanSpan(inside, lo, hi)
  // Shrink inward past cells that are not inside
  steps = 0
  while (x0 <= x1 && !inside(x0)) { x0++; if (++steps > MAX_FIXUP_STEPS) return scanSpan(inside, lo, hi) }
  steps = 0
  while (x1 >= x0 && !inside(x1)) { x1--; if (++steps > MAX_FIXUP_STEPS) return scanSpan(inside, lo, hi) }
  if (x0 > x1) return null
  return [x0, x1]
}

function scanSpan(inside: (x: number) => boolean, lo: number, hi: number): Span | null {
  let x0 = Number.NaN, x1 = Number.NaN
  for (let x = lo; x <= hi; x++) {
    if (!inside(x)) continue
    if (Number.isNaN(x0)) x0 = x
    x1 = x
  }
  return Number.isNaN(x0) ? null : [x0, x1]
}

// ----------------------------------------------------------------------------
// Row ranges (which (y, z) rows a primitive can touch)
// ----------------------------------------------------------------------------

export type RowRange = { y0: number; y1: number; z0: number; z1: number }

/** Inclusive cell range of rows that may intersect the primitive (a superset). */
export function rowRange(p: BrushPrim): RowRange {
  const r = p.rFx
  if (p.kind === 'sphere') {
    return {
      y0: Math.ceil((p.c[1] - r) / FX_ONE), y1: Math.floor((p.c[1] + r) / FX_ONE),
      z0: Math.ceil((p.c[2] - r) / FX_ONE), z1: Math.floor((p.c[2] + r) / FX_ONE),
    }
  }
  return {
    y0: Math.ceil((Math.min(p.a[1], p.b[1]) - r) / FX_ONE), y1: Math.floor((Math.max(p.a[1], p.b[1]) + r) / FX_ONE),
    z0: Math.ceil((Math.min(p.a[2], p.b[2]) - r) / FX_ONE), z1: Math.floor((Math.max(p.a[2], p.b[2]) + r) / FX_ONE),
  }
}

// ----------------------------------------------------------------------------
// Round cone (tapered capsule): the convex hull of sphere(a, ra) and sphere(b, rb)
// ----------------------------------------------------------------------------

/**
 * Is the fixed-point point (X, Y, Z) inside the round cone from sphere(a, ra) to sphere(b, rb)?
 *
 * The two end spheres are tested exactly (integer arithmetic, same rule as `insideFx` for a sphere),
 * so a round cone always holds every cell of both end spheres: the tapered sweep (sweep.ts) relies on
 * that for its crescent decomposition. The tangent cone between them is tested in plain IEEE double
 * arithmetic (Inigo Quilez's round-cone distance, single square root). JavaScript fixes + - * / and
 * `Math.sqrt` to correctly rounded doubles, so the result is the same on every device even where it
 * is not bit-exact against the real-valued shape; replay stays deterministic.
 */
export function roundConeContainsFx(a: Fx3, ra: number, b: Fx3, rb: number, X: number, Y: number, Z: number): boolean {
  const pax = X - a[0], pay = Y - a[1], paz = Z - a[2]
  if (pax * pax + pay * pay + paz * paz <= ra * ra) return true
  const pbx = X - b[0], pby = Y - b[1], pbz = Z - b[2]
  if (pbx * pbx + pby * pby + pbz * pbz <= rb * rb) return true
  const dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2]
  const L2 = dx * dx + dy * dy + dz * dz
  const rr = ra - rb
  const a2 = L2 - rr * rr
  // One sphere holds the other (or the segment is degenerate): the hull is the bigger sphere.
  if (L2 === 0 || a2 <= 0) return false
  const y = pax * dx + pay * dy + paz * dz
  const z = y - L2
  const qx = pax * L2 - dx * y, qy = pay * L2 - dy * y, qz = paz * L2 - dz * y
  const x2 = qx * qx + qy * qy + qz * qz
  const y2 = y * y * L2
  const z2 = z * z * L2
  const k = Math.sign(rr) * rr * rr * x2
  // Closest feature is one of the end spheres, already tested exactly above.
  if (Math.sign(z) * a2 * z2 > k) return false
  if (Math.sign(y) * a2 * y2 < k) return false
  // Distance to the cone surface: (sqrt(x2 * a2 / L2) + y * rr) / L2 - ra <= 0
  return Math.sqrt((x2 * a2) / L2) + y * rr <= ra * L2
}
